const Log = require("@/utils/Log").default;
const UrlToActivityMapping = require("./UrlToActivityMapping");

export class Position{
    /**
     * @param {string} path 
     */
    constructor(path){
        /** @type {string} */
        this.path = path;

        /** @type {number} */
        this.length = path.length;

        /** @type {number} */
        this.next  = 0;        
    }

    /**
     * @return {boolean}
     */
    hasNext() {
        return this.next < this.length;
    }

    /**
     * @return {string}
     */
    whatIsNext() {
        if(!this.hasNext()){
            return null;
        }
        return this.path.charAt(this.next);
    }

    /**
     * @return {string}
     */
    peekNextChar() {
        if(!this.hasNext()){
            return null;
        }        
        return this.path.charAt(this.next++);
    }

    stepBack() {
        this.next--;
    }

}

export class RunListEntry{
    constructor(){
         /** @type {string} */
        this.activityCode = '';

         /** @type {Prop[]} */
        this.propsList = [];

        /** @type {*} */
        this.activityClass = null;

         /** @type {Object.<string, string>} */
         this.validatedProps = {};

    }
}

export class Prop{
    constructor(){
        /** @type {string} */
        this.name = '';   

        /** @type {string} */
        this.value = '';   
    }
}


export class ActivityUrlDecoder{
    /**
     * @param {string} path 
     * @param {Object.<string, UrlToActivityMapping>} urlToActivityMapping
     * @returns {{errors:boolean, parseErrors: boolean, activityMappingErrors: boolean, propsMappingErrors:boolean, activities: RunListEntry[]}} 
     */
    decode(path, urlToActivityMapping){
        path = decodeURIComponent(path);
        // activity#p1=abc&p2=def/activity

        /** @type {Position} */
        let pos = new Position(path);

        /** @type {RunListEntry[]} */
        let runList = [];

        let parseErrors = !this.decodeActivityName(pos, runList);
        let activityMappingErrors = false;
        let propsMappingErrors = false;

        let validatedList = [];
        for(let i=0; i<runList.length; i++){
            let runEntry = runList[i];
            if(!Object.prototype.hasOwnProperty.call(urlToActivityMapping, runEntry.activityCode)){
                Log.debug("No mapping found for activity code: " + runEntry.activityCode);
                activityMappingErrors = true;
                break;
            }
            
            let mapping = urlToActivityMapping[runEntry.activityCode];
            runEntry.activityClass = mapping.activityClass;
            runEntry.validatedProps = {};
            runEntry.propsList.forEach(prop =>{
                if(Object.prototype.hasOwnProperty.call(runEntry.validatedProps, prop.name)){
                    Log.debug("Property duplicated:" + runEntry.activityCode + ":" + prop.name);
                    propsMappingErrors = true;
                    return;
                }

                if(!mapping.exposedProps.includes(prop.name)) {
                    Log.debug("Property not exposed:" + runEntry.activityCode + ":" + prop.name);
                    propsMappingErrors = true;
                    return;
                }

                runEntry.validatedProps[prop.name] = prop.value;
            });
            
            let missing = mapping.exposedProps.filter(p => runEntry.validatedProps[p] === undefined);
            if(missing.length > 0){
                Log.debug("Not all properties exposed in " + runEntry.activityCode + ". Missing props: " + missing.join(","));
                propsMappingErrors = true;
            } else{
                validatedList.push(runEntry);
            }            
        }

        let errors = (parseErrors || propsMappingErrors || activityMappingErrors);       

        return {errors, parseErrors, propsMappingErrors,  activityMappingErrors, 'activities' : validatedList};
    }

    /**
     * @param {Position} pos 
     * @param {RunListEntry[]} runList 
     * @return {boolean}
     */
    decodeActivityName(pos, runList){        
        while(pos.whatIsNext() == "/"){
            pos.peekNextChar();
        }

        // now pos is on the first expected char or prop name

        // /
        if(!pos.hasNext()){
            return true;
        }

        /** @type {RunListEntry} */
        let runListEntry = new RunListEntry();

        while(this.isAllowedInActivityName(pos.whatIsNext())){
            runListEntry.activityCode += pos.peekNextChar();
        }

        // /activity
        if(!pos.hasNext()){
            runList.push(runListEntry);
            return true;
        }

        /** @type {string} */
        let delimiter = pos.peekNextChar();
        
        // /activity/activity2...
        if(delimiter == "/"){
            runList.push(runListEntry);
            return this.decodeActivityName(pos, runList);
        }

        // /activity@
        if(delimiter != '#'){
            Log.debug("Bad activity chain url, unknown character, # expected; error at char " + pos.next + ": '" + delimiter + "' in: '" + pos.path + "'");        
            return false;
        }
        // /#
        if(runListEntry.activityCode == ""){
            Log.debug("Bad activity chain url, activity code empty; error at char " + pos.next + ": '" + delimiter + "' in: '" + pos.path + "'");
            return false;
        }


        return this.readProp(pos, runList, runListEntry);
    }

    /**
     * @param {Position} pos 
     * @param {RunListEntry[]} runList 
     * @param {RunListEntry} runListEntry
     * @return {boolean}
     */
    readProp(pos, runList, runListEntry){      

        let prop = new Prop();

        // pos is on the first expected char or prop name

        while(this.isAllowedInPropName(pos.whatIsNext())){
            prop.name += pos.peekNextChar();
        }

        // /activity#&
        if(prop.name == ''){
            Log.debug("Bad activity chain url, property name empty; error at char " + pos.next);
            return false;
        }

        // /activity#aa
        if(!pos.hasNext()){
            runListEntry.propsList.push(prop);
            runList.push(runListEntry);
            return true;
        }

        let delimiter = pos.peekNextChar();

        // /activity#aa@
        if(delimiter != '='){
            Log.debug("Bad activity chain url, unknown character, = expected; error at char " + pos.next + ": '" + delimiter + "' in: '" + pos.path + "'");        
            return false;
        }

        prop.value = this.readPropValue(pos);
        runListEntry.propsList.push(prop);

        // /activity#aa=val
        if(!pos.hasNext()){
            runList.push(runListEntry);
            return true;
        }

        delimiter = pos.peekNextChar();

        // /activity#aa=val/activity2
        if(delimiter == "/"){
            runList.push(runListEntry);
            return this.decodeActivityName(pos, runList);

        }

        // /activity#aa=val&bb=val2
        if(delimiter == "&"){
            return this.readProp(pos, runList, runListEntry);

        }

        Log.debug("Can't reach this place...");
        return false;
    }



    /**
     * @param {Position} pos 
     * @return {string} 
     */
    readPropValue(pos){
        let result = '';
        while(pos.hasNext()){
            let char = pos.peekNextChar();
            if(char == "/"){
                if(pos.whatIsNext() == "/"){
                    char = pos.peekNextChar();
                } else {
                    pos.stepBack();
                    return result;
                }
            }

            if(char == "&"){
                if(pos.whatIsNext() == "&"){
                    char = pos.peekNextChar();
                } else {
                    pos.stepBack();
                    return result;
                }
            }

            result += char;
        }
        return result;
    }

    /**
     * @param {string} character 
     * @return {boolean} 
     */
    isAllowedInActivityName(character){
        if(character == null){
            return false;
        }
        return character.match(/^[a-z0-9\-]$/);
    }

        /**
     * @param {string} character 
     * @return {boolean} 
     */
    isAllowedInPropName(character){
        if(character == null){
            return false;
        }
        return character.match(/^[A-Za-z0-9\-_.,$@]$/);
    }
}



ActivityUrlDecoder.instance = new ActivityUrlDecoder();

var inst = ActivityUrlDecoder.instance;

export { inst as default };